import { BoundsRect, IMapInstance, IMapMarker, LatLng, MarkerOptions, RouteLineMode } from "../map-provider.interface";
import { HereMarker } from "./here-marker";

type HereModule = typeof import('@here/maps-api-for-javascript');

type HereMap = InstanceType<HereModule["Map"]>;
type HereUI = InstanceType<HereModule["ui"]["UI"]>;
type HereBehavior = InstanceType<HereModule["mapevents"]["Behavior"]>;
type HerePlatform = InstanceType<HereModule["service"]["Platform"]>;
type HereMapObject = InstanceType<HereModule["map"]["Object"]>;
type HereIcon = InstanceType<HereModule["map"]["Icon"]>;
type HerePolyline = InstanceType<HereModule["map"]["Polyline"]>;
type HereInfoBubble = InstanceType<HereModule["ui"]["InfoBubble"]>;
type HereAbstractGeometry = InstanceType<HereModule["geo"]["AbstractGeometry"]>;
type HereEvent = HereModule["util"]["Event"];

export class HereMapInstance implements IMapInstance {
    private readonly _here: HereModule;
    private readonly _map: HereMap;
    private readonly _ui: HereUI;
    private readonly _behavior: HereBehavior;
    private readonly _container: HTMLElement;
    private readonly _iconCache = new Map<string, Promise<HereIcon>>();
    private readonly _platform: HerePlatform;
    private readonly _objectsOnMap = new Set<HereMapObject>();
    private _routePolyline?: HerePolyline;

    private _boundsHandler: ((evt: HereEvent) => void) | null = null;
    private _idleHandler: ((evt: HereEvent) => void) | null = null;

    private _markers: HereMarker[] = [];
    private _bubble: HereInfoBubble | null = null;

    constructor(here: HereModule, map: HereMap, ui: HereUI, behavior: HereBehavior, platform: HerePlatform, container: HTMLElement) {
        this._here = here;
        this._map = map;
        this._ui = ui;
        this._behavior = behavior;
        this._container = container;
        this._platform = platform;
    }

    // ---------------------------------------------
    // Marker APIs
    // ---------------------------------------------
    async addMarkerAsync(coords: LatLng, options?: MarkerOptions): Promise<IMapMarker> {
        let markerObj: HereMapObject;

        // Prefer DOM marker if HTML content was supplied
        if (options?.iconUrl) {
            // Pin tip at bottom-center.
            const baseW = 30;
            const baseH = 30;
            const scale = options.scale ?? 1;

            const w = Math.max(1, Math.round(baseW * scale));
            const h = Math.max(1, Math.round(baseH * scale));

            const icon = await this._getIconAsync(options.iconUrl, w, h);
            markerObj = new this._here.map.Marker(coords, { icon, data: null });
        } else {
            markerObj = new this._here.map.Marker(coords);
        }

        this._map.addObject(markerObj);
        this._objectsOnMap.add(markerObj);

        const handle = new HereMarker(this._map, markerObj);
        this._markers.push(handle);

        if (options?.center) {
            this._map.setCenter(coords);
            if (typeof options.zoom === "number")
                this._map.setZoom(options.zoom);
        }

        return handle;
    }

    removeMarker(marker: IMapMarker): void {
        if (!marker) return;

        const native = (marker as any)._native?.() as HereMapObject | undefined;
        if (native && this._objectsOnMap.has(native)) {
            this._map.removeObject(native);
            this._objectsOnMap.delete(native);
        }

        // Remove from internal list
        this._markers = this._markers.filter(m => m !== marker);

        // Optional: if your info bubble is tied to this marker, close it.
        // If you can’t tell, you can skip this, or just always close.
        this.closeInfo();
    }

    clearMarkers(): void {
        // Remove markers from the map
        const native = this._markers.map(m => m._native());
        if (native.length > 0) {
            this._map.removeObjects(native);
            native.forEach(x => this._objectsOnMap.delete(x));
        }

        // Reset the markers array
        this._markers = [];

        // Close any open info bubble (if you implemented one)
        this.closeInfo();
    }

    // ---------------------------------------------
    // Routing / Overlays APIs
    // ---------------------------------------------
    async drawRouteAsync(origin: LatLng, destination: LatLng, mode?: RouteLineMode): Promise<void> {
        // Remove old route if present
        this.clearRoute();

        if (mode === "straight")
            this._drawStraightRoute(origin, destination);
        else if (mode === "route")
            await this._drawTrueRouteAsync(origin, destination);
    }

    public clearRoute(): void {
        if (this._routePolyline) {
            this._map.removeObject(this._routePolyline);
            this._routePolyline = undefined;
        }
    }

    // ---------------------------------------------
    // Info window APIs
    // ---------------------------------------------
    openInfo(anchor: IMapMarker, html: string | HTMLElement): void {
        const native = (anchor as HereMarker)._native();
        const coords = (native as any).getGeometry?.() as { lat: number; lng: number } | undefined;
        if (!coords)
            return;

        // Create content as an element (avoids some oddities with string content)
        const content = document.createElement("div");

        if (typeof html === "string") {
            content.innerHTML = html;
        } else {
            content.appendChild(html);
        }

        // Move bubble up by 40px so it doesn't cover the pin
        const bubbleCoords = this._offsetGeoByPixels(coords, 0, -30);

        // Reuse one bubble
        if (this._bubble) {
            this._bubble.setPosition(bubbleCoords);
            this._bubble.setContent(content);
            this._bubble.open();
        } else {
            this._bubble = new this._here.ui.InfoBubble(bubbleCoords, { content });
            this._ui.addBubble(this._bubble);
        }
    }

    closeInfo(): void {
        if (!this._bubble) return;

        this._bubble.close();
    }

    // ---------------------------------------------
    // Viewport / Camera APIs
    // ---------------------------------------------
    setCenter(coords: LatLng): void {
        this._map.setCenter(coords);
    }

    getZoom(): number {
        // HERE returns number (can be fractional)
        return this._map.getZoom() ?? 0;
    }

    setZoom(zoom: number): void {
        this._map.setZoom(zoom);
    }

    isInView(coords: LatLng): boolean {
        const rect = this._container.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;

        // hidden / not laid out yet
        if (width <= 0 || height <= 0) return false;

        const screenPoint = this._map.geoToScreen(new this._here.geo.Point(coords.lat, coords.lng));
        if (!screenPoint) return false;

        return (
            screenPoint.x >= 0 &&
            screenPoint.y >= 0 &&
            screenPoint.x <= width &&
            screenPoint.y <= height
        );
    }

    resize(): void {
        this._map.getViewPort().resize();
    }

    // ---------------------------------------------
    // Clustering APIs
    // ---------------------------------------------
    setClusterMarkers(markers: IMapMarker[]): void {
        // Not implemented for HERE yet.
        // Later: implement using this._here.clustering.Provider + ObjectLayer.
    }

    // ---------------------------------------------
    // Bounds APIs
    // ---------------------------------------------
    getBoundsRect(): BoundsRect | null {
        const lookAt = this._map.getViewModel().getLookAtData();
        const b: any = lookAt?.bounds;
        if (!b) return null;

        // this._here.geo.Rect usually exposes top/bottom/left/right or getters
        const north = b.getTop?.() ?? b.top;
        const south = b.getBottom?.() ?? b.bottom;
        const west = b.getLeft?.() ?? b.left;
        const east = b.getRight?.() ?? b.right;

        if ([north, south, east, west].some(v => typeof v !== "number"))
            return null;

        return { north, south, east, west };
    }

    fitBoundsRect(bounds: BoundsRect): void {
        const padded = this._padBounds(bounds, 0.25);

        // this._here.geo.Rect(top, left, bottom, right) => (north, west, south, east)
        const rect = new this._here.geo.Rect(padded.north, padded.west, padded.south, padded.east);

        // Fit the map to the rect
        this._map.getViewModel().setLookAtData({ bounds: rect as any });
    }

    // ---------------------------------------------
    // Events
    // ---------------------------------------------
    onIdle(handler: () => void): void {
        // Remove previous handler if present
        if (this._idleHandler) {
            this._map.removeEventListener("mapviewchangeend", this._idleHandler);
            this._idleHandler = null;
        }

        // Keep a stable reference so we can remove it later
        this._idleHandler = () => handler();

        this._map.addEventListener("mapviewchangeend", this._idleHandler)
    }

    onBoundsChanged(handler: () => void): () => void {
        // Replace prior subscription (single active subscription per instance)
        if (this._boundsHandler) {
            this._map.removeEventListener("mapviewchange", this._boundsHandler);
            this._boundsHandler = null;
        }

        // HERE will call us with an event; ignore it and just forward
        const wrapped = (_evt: HereEvent) => handler();
        this._boundsHandler = wrapped;

        this._map.addEventListener("mapviewchange", wrapped);

        // return unsubscribe
        return () => {
            // only remove if still the active one
            if (this._boundsHandler === wrapped) {
                this._map.removeEventListener("mapviewchange", wrapped);
                this._boundsHandler = null;
            } else {
                // fallback: still remove the handler we created
                this._map.removeEventListener("mapviewchange", wrapped);
            }
        };
    }

    // ---------------------------------------------
    // Helpers
    // ---------------------------------------------
    private async _getIconAsync(url: string, maxH: number, minH: number = 0): Promise<HereIcon> {
        const key = `${url}|${maxH}|${minH}`;
        let p = this._iconCache.get(key);
        if (!p) {
            p = this._createIconFromUrlAsync(url, maxH, minH);
            this._iconCache.set(key, p);
        }
        return p;
    }

    private async _createIconFromUrlAsync(url: string, maxHeightPx: number, minHeightPx: number = 0): Promise<HereIcon> {
        // Load the SVG into an <img> to get intrinsic size
        const img = new Image();
        img.decoding = "async";
        img.src = url;

        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error(`Failed to load icon: ${url}`));
        });

        let naturalW = img.naturalWidth || 0;
        let naturalH = img.naturalHeight || 0;

        // Some SVGs report 0; provide a reasonable fallback aspect (pin-ish)
        if (naturalW <= 0 || naturalH <= 0) {
            naturalW = 23;
            naturalH = 30;
        }

        // Decide target height: clamp intrinsic height into [min, max]
        const targetH = Math.min(Math.max(naturalH, minHeightPx), maxHeightPx);

        // Scale to reach target height, preserve aspect ratio
        const scale = targetH / naturalH;
        const w = Math.round(naturalW * scale);
        const h = Math.round(targetH);

        return new this._here.map.Icon(url, {
            size: { w, h },
            anchor: { x: w / 2, y: h } // bottom-center
        });
    }

    private _offsetGeoByPixels(coords: LatLng, dx: number, dy: number): LatLng {
        const p = this._map.geoToScreen(new this._here.geo.Point(coords.lat, coords.lng));
        if (!p) return coords;

        const moved = new this._here.math.Point(p.x + dx, p.y + dy);
        const geo = this._map.screenToGeo(moved.x, moved.y);

        return { lat: geo?.lat ?? 0, lng: geo?.lng ?? 0 };
    }

    private _padBounds(bounds: BoundsRect, paddingPercent = 0.1): BoundsRect {
        const latSpan = bounds.north - bounds.south;
        const lngSpan = bounds.east - bounds.west;

        const latPad = latSpan * paddingPercent;
        const lngPad = lngSpan * paddingPercent;

        return {
            north: bounds.north + latPad,
            south: bounds.south - latPad,
            east: bounds.east + lngPad,
            west: bounds.west - lngPad
        };
    }

    private _drawStraightRoute(origin: LatLng, destination: LatLng): void {
        const lineString = new this._here.geo.LineString();
        lineString.pushPoint(origin);
        lineString.pushPoint(destination);

        this._routePolyline = new this._here.map.Polyline(lineString, {
            data: { kind: "route", mode: "straight" }, // required by your typings
            style: {
                lineWidth: 4,
                strokeColor: "#ED1C24"
            } as any
        });

        this._map.addObject(this._routePolyline);
    }

    private async _drawTrueRouteAsync(origin: LatLng, destination: LatLng): Promise<void> {
        // HERE Routing v8 service
        const router = this._platform.getRoutingService();

        const params: any = {
            transportMode: "car",
            origin: `${origin.lat},${origin.lng}`,
            destination: `${destination.lat},${destination.lng}`,
            return: "polyline"
        };

        const result = await new Promise<any>((resolve, reject) => {
            router.calculateRoute(params, resolve, (err: any) => reject(this._toError(err)));
        });

        const polyline = result?.routes?.[0]?.sections?.[0]?.polyline;
        if (!polyline) return;

        // Decode flexible polyline into a LineString
        const lineString = this._here.geo.LineString.fromFlexiblePolyline(polyline);

        // Create a style object the SDK understands
        const routeStyle = new this._here.map.SpatialStyle({
            lineWidth: 4,          // <-- HERE SpatialStyle uses lineWidth
            strokeColor: "#ED1C24" // pick your color or omit to use default
        });

        // Create the polyline object and add to map
        this._routePolyline = new this._here.map.Polyline(lineString, {
            data: { kind: "route", transportMode: "car" }, // required by your typings
            style: routeStyle
        });

        this._map.addObject(this._routePolyline);

        // Optional: fit the map to the route
        const rect = this._routePolyline.getBoundingBox();
        this._map.getViewModel().setLookAtData({
            bounds: rect as unknown as HereAbstractGeometry
        });
    }

    private _toError(reason: unknown, fallback = "Routing service error"): Error {
        if (reason instanceof Error) return reason;

        const msg =
            typeof reason === "string"
                ? reason
                : (reason as any)?.message ?? fallback;

        const e = new Error(msg);
        (e as any).cause = reason;
        return e;
    }
}